import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { addPounceLead } from "../../Store/apiStore";
import HeaderBar from "../HeaderBar/HeaderBar";
import OutboundCallerBrand from "../OutboundCallerBrand/OutboundCallerBrand";
import PounceToggle from "../PounceLeadsPage/PounceToggle/PounceToggle";
import PounceFooterNav from "../PounceAgentDetail/PounceFooterNav/PounceFooterNav";
import PounceLeadForm from "../FastAgentDetails/PounceOverview/PounceLeadForm";
import LockOverlay from "../LockOverlay/LockOverlay";
import usePounceAccess, {
  POUNCE_LOCK_MESSAGE,
} from "../../hooks/usePounceAccess";
import styles from "./PounceAddLeadPage.module.css";

const EMPTY_FORM = {
  name: "",
  phone: "",
  email: "",
  company: "",
  source: "manual",
  inquiry: "",
};

const errorText = (err, fallback) =>
  err?.response?.data?.message || err?.response?.data?.error || fallback;

const errorField = (message) => {
  if (/phone|number/i.test(message)) return "phone";
  if (/e-?mail/i.test(message)) return "email";
  if (/name/i.test(message)) return "name";
  return null;
};

const PounceAddLeadPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { canEdit } = usePounceAccess();

  // Keep the return path internal; direct visits fall back to Home.
  const requestedReturnTo = location.state?.returnTo;
  const returnTo =
    typeof requestedReturnTo === "string" && requestedReturnTo.startsWith("/")
      ? requestedReturnTo
      : "/fast-agent-detail";

  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Show success feedback briefly, then return to the screen that opened this page.
  useEffect(() => {
    if (!success) return undefined;
    const timer = window.setTimeout(() => {
      navigate(returnTo, {
        replace: true,
        state: {
          pounceLeadAddedAt: Date.now(),
          pounceLeadSuccess: success,
        },
      });
    }, 1300);
    return () => window.clearTimeout(timer);
  }, [success, navigate, returnTo]);

  const goBack = () => navigate(returnTo);

  const submit = async (e) => {
    e.preventDefault();
    if (!canEdit) {
      setError(POUNCE_LOCK_MESSAGE);
      return;
    }

    setSaving(true);
    setError("");
    try {
      // PounceLeadForm's required consent checkbox must pass browser validation
      // before this handler runs. The API helper expects these manual-add values.
      const res = await addPounceLead({
        ...form,
        consent: true,
        consent_source: "manual",
      });

      const message = res?.duplicate
        ? "That lead is already being handled."
        : res?.paused
          ? "Lead added. Outbound Caller is off, so it's on hold."
          : "Lead added — calling now.";

      setSuccess(message);
      setForm(EMPTY_FORM);
    } catch (err) {
      setError(errorText(err, "We couldn't add that lead. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  const fieldError = errorField(error);

  return (
    <div className={styles.page}>
      <div className={styles.stickyTop}>
        <HeaderBar
          title=""
          subtitle={<OutboundCallerBrand />}
          rightIcon={<PounceToggle />}
          onBack={goBack}
        />
      </div>

      <main className={styles.main}>
        <h1 className={styles.title}>Add Lead</h1>
        <p className={styles.subtitle}>
          Add a new lead and Pounce will contact them automatically.
        </p>

        <button
          className={styles.bulkCard}
          type="button"
          aria-label="Bulk Upload Leads"
          onClick={() =>
            navigate("/bulk-upload", {
              state: { returnTo: location.pathname },
            })
          }
        >
          <span className={styles.uploadIcon} aria-hidden="true">
            <svg width="19" height="19" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5M4.5 15.5v3A2.5 2.5 0 0 0 7 21h10a2.5 2.5 0 0 0 2.5-2.5v-3"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className={styles.bulkCopy}>
            <strong className={styles.bulkTitle}>Bulk Upload Leads</strong>
            <span className={styles.bulkDescription}>
              Upload CSV or spreadsheet to add multiple leads at once.
            </span>
          </span>
          <svg
            className={styles.chevron}
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="m9 18 6-6-6-6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {error && !fieldError && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}
        {success && (
          <p className={styles.success} role="status">
            {success}
          </p>
        )}

        <LockOverlay locked={!canEdit} message={POUNCE_LOCK_MESSAGE}>
          <div className={styles.formSlot}>
            <PounceLeadForm
              form={form}
              fieldErrors={fieldError ? { [fieldError]: error } : undefined}
              onFieldChange={(name, value) => {
                setForm((current) => ({ ...current, [name]: value }));
                if (errorField(error) === name) setError("");
              }}
              onSubmit={submit}
              saving={saving}
              submitLabel="Add Lead & Start Pounce"
            />
          </div>
        </LockOverlay>

        {/* <button className={styles.saveForLater} type="button">
          Save for later
        </button> */}
      </main>

      <PounceFooterNav active="leads" />
    </div>
  );
};

export default PounceAddLeadPage;
